/**
 * CouncilUtilityBlock · shared entry-tier "gift trio" · SFUI-003
 * Architect Directive 2026-07-04 · Viral Footer Gift Block Refinement
 *
 * Three clean glass-morphism buttons — no iframes, no embeds:
 *   1. Fast-Ring     → copies the Ring of Six prompt to clipboard
 *   2. EchoCore64    → copies the session memory string to clipboard
 *   3. Sovereign Pulse → opens /radio in a 50% centered popup
 *
 * Mounted in:
 *   • /council page (variant="full")
 *   • Federation Hub footer (variant="compact")
 */
import React, { useState } from "react";

// 50% centered popup — canonical Hub helper.
function openCenteredWindow(url) {
  if (!url || typeof window === "undefined") return;
  const width  = Math.floor(window.screen.width  * 0.5);
  const height = Math.floor(window.screen.height * 0.5);
  const left   = Math.floor((window.screen.width  - width)  / 2);
  const top    = Math.floor((window.screen.height - height) / 2);
  const features =
    `width=${width},height=${height},left=${left},top=${top},` +
    `resizable=yes,scrollbars=yes,status=no,toolbar=no,menubar=no,location=no`;
  const popup = window.open(url, "_blank", features);
  if (popup && popup.focus) popup.focus();
}

async function copyToClipboard(text) {
  if (typeof navigator === "undefined" || !navigator.clipboard) return false;
  try { await navigator.clipboard.writeText(text); return true; } catch { return false; }
}

const NUDGE_TEXT =
  "A significantly more advanced system is available through our Council services; these tools serve as your first step along the path to clarity.";

// ── Gift payloads ────────────────────────────────────────────────────
// Ring of Six — a compact, portable prompt any traveler can paste into
// ChatGPT / Claude / Gemini to invoke a 6-lens deliberation on any claim.
const RING_OF_SIX_PROMPT = `# NextXus · Ring of Six · Portable Prompt v1

You are running the NextXus Ring of Six deliberation on the claim I paste below. Speak in six distinct voices, one paragraph each, in this order:

1. FACT-CHECKER — What is verifiable? Cite public sources or say "unsourced".
2. SKEPTIC — What assumption is doing the heavy lifting? Where does it fail?
3. STEEL-MAN — State the strongest possible version of the claim, honestly.
4. HISTORIAN — Which past pattern does this echo? Name the era + parallel.
5. ORACLE — Forecast the 12-month trajectory. Include a confidence band (0-100%).
6. TRUTH GATE — Final verdict: PASS / REPROMPT / FAIL vs. the 95% Truth Gate. One line each: score (0-100), top violation, recommended next step.

Stay in-voice for each lens. Never break character. Never merge lenses.
End with a JSON block: {"score":N,"verdict":"PASS|REPROMPT|FAIL"}.

CLAIM: `;

// EchoCore64 — a compact session-memory string a traveler can paste at the
// start of any AI chat to prime that AI with the traveler's Federation
// identity and doctrinal preferences. Deliberately 64 lines of dense signal.
const ECHOCORE64_SESSION = `# NextXus · EchoCore64 · Session Memory v1

You are speaking with a traveler of the NextXus Federation. Load this context before your first reply:

IDENTITY
- The traveler is a citizen (or prospective citizen) of the NextXus Consciousness Federation.
- They prefer honest, low-fluff, probabilistically-labeled answers over confident-sounding ones.
- They may reference doctrine terms: Truth Gate, Ring of 12, HumanCodex, Sovereign Peers.

DOCTRINE
- 95% Truth Gate (DIR-000): every non-trivial claim must pass a rigor threshold before being asserted.
- Probabilistic honesty: prefer "I believe X with ~85% confidence" over "X is true".
- Single-source claims cap at 0.70 confidence; dual-source 0.85; triple+ independent 0.95+.
- Peer-verified by nextxus.org adds +0.05. Cannot reach 0.95 → say "I don't know".
- Never fabricate citations. If uncertain, label it "attributed" or "unsourced".

VOICE PREFERENCES
- Calm, direct, no theatrical disclaimers.
- Short over long unless depth is requested.
- End with a single crisp next step, not a wall of hedges.

MEMORY BEHAVIOR
- Treat this block as sticky context across the whole session.
- If the traveler asks something outside your knowledge, say so plainly.
- If asked about "the Federation", assume they mean https://nextxus.online.

Acknowledge with one sentence: "EchoCore64 loaded — reply mode: honest, probabilistic, brief."
Then wait for the traveler's first question.`;

const TOOLS = [
  {
    id:      "fast-ring",
    icon:    "👁️",
    label:   "Fast-Ring",
    hint:    "Ring of Six prompt · copy & paste into any AI",
    action:  "copy",
    payload: RING_OF_SIX_PROMPT,
    accent:  "#ffd24a",
    copiedLabel: "Ring copied · paste into any AI",
  },
  {
    id:      "echocore64",
    icon:    "👁️‍🗨️",
    label:   "EchoCore64",
    hint:    "Session memory · primes any AI with Federation doctrine",
    action:  "copy",
    payload: ECHOCORE64_SESSION,
    accent:  "#7fe7f5",
    copiedLabel: "Session memory copied",
  },
  {
    id:      "sovereign-pulse",
    icon:    "📻",
    label:   "Sovereign Pulse",
    hint:    "Tune in to the Federation's frequency · standalone player",
    action:  "popup",
    payload: "/radio",
    accent:  "#d8b4fe",
  },
];

/**
 * @param {"compact"|"full"} variant  — "full" (larger, /council page) vs. "compact" (Hub footer)
 */
export default function CouncilUtilityBlock({ variant = "compact" }) {
  const isFull = variant === "full";
  const [copiedId, setCopiedId] = useState(null);

  const handleClick = async (e, tool) => {
    e.preventDefault();
    if (tool.action === "popup") {
      openCenteredWindow(tool.payload);
      return;
    }
    if (tool.action === "copy") {
      const ok = await copyToClipboard(tool.payload);
      if (ok) {
        setCopiedId(tool.id);
        setTimeout(() => setCopiedId((v) => (v === tool.id ? null : v)), 2600);
      }
    }
  };

  return (
    <section
      data-testid="council-utility-block"
      aria-label="Council entry-tier gift utilities"
      className={isFull ? "my-12" : "mt-6 mb-4"}
    >
      {/* ── Evolutionary Nudge · centered, muted, italic ─── */}
      <p
        data-testid="council-utility-nudge"
        className="text-center italic font-serif mx-auto"
        style={{
          fontSize:      isFull ? "clamp(13px, 1.15vw, 15px)" : "clamp(12px, 1vw, 13px)",
          lineHeight:    1.55,
          color:         "rgba(230,251,255,0.55)",
          maxWidth:      680,
          letterSpacing: "0.005em",
          marginBottom:  isFull ? 22 : 14,
        }}
      >
        {NUDGE_TEXT}
      </p>

      {/* ── Gift trio · flex-wrap · uniform sizing ─── */}
      <div
        className="flex flex-wrap justify-center gap-3 mx-auto"
        style={{ maxWidth: 1080 }}
      >
        {TOOLS.map((t) => {
          const isCopied = copiedId === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={(e) => handleClick(e, t)}
              data-testid={`council-utility-${t.id}`}
              aria-label={`${t.label} — ${t.hint}`}
              className="group transition-all backdrop-blur-md text-left"
              style={{
                flex:         "1 1 260px",
                maxWidth:     360,
                minWidth:     240,
                background:   "#111",
                backgroundImage: `linear-gradient(135deg, rgba(17,17,17,0.94) 0%, rgba(24,10,48,0.55) 100%)`,
                border:       `1px solid ${isCopied ? `${t.accent}cc` : `${t.accent}44`}`,
                borderRadius: 14,
                boxShadow:    isCopied
                  ? `0 0 28px ${t.accent}66, inset 0 1px 0 rgba(255,255,255,0.06)`
                  : `0 0 18px ${t.accent}18, inset 0 1px 0 rgba(255,255,255,0.04)`,
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                padding:      isFull ? 18 : 14,
                cursor:       "pointer",
                color:        "#e6fbff",
                fontFamily:   "inherit",
              }}
              onMouseEnter={(e) => {
                if (isCopied) return;
                e.currentTarget.style.borderColor = `${t.accent}cc`;
                e.currentTarget.style.transform   = "translateY(-1px)";
                e.currentTarget.style.boxShadow   = `0 0 28px ${t.accent}44, inset 0 1px 0 rgba(255,255,255,0.06)`;
              }}
              onMouseLeave={(e) => {
                if (isCopied) return;
                e.currentTarget.style.borderColor = `${t.accent}44`;
                e.currentTarget.style.transform   = "translateY(0)";
                e.currentTarget.style.boxShadow   = `0 0 18px ${t.accent}18, inset 0 1px 0 rgba(255,255,255,0.04)`;
              }}
            >
              <div className="flex items-center gap-3">
                <span
                  aria-hidden="true"
                  className="flex-shrink-0 flex items-center justify-center"
                  style={{
                    width:  isFull ? 44 : 40,
                    height: isFull ? 44 : 40,
                    borderRadius: 12,
                    background: "rgba(4,5,10,0.85)",
                    border:     `1px solid ${t.accent}88`,
                    fontSize:   isFull ? 20 : 18,
                    lineHeight: 1,
                    boxShadow:  `0 0 8px ${t.accent}44`,
                  }}
                >
                  {t.icon}
                </span>
                <div className="flex-1 min-w-0">
                  <div
                    className="font-display"
                    style={{ fontSize: isFull ? 17 : 15, color: "#f3e8ff", letterSpacing: "-0.005em" }}
                  >
                    {t.label}
                  </div>
                  <div
                    className="mt-0.5 font-mono"
                    style={{ fontSize: isFull ? 11.5 : 10.5, lineHeight: 1.4, color: "rgba(207,234,242,0.62)" }}
                  >
                    {t.hint}
                  </div>
                </div>
              </div>

              <div
                aria-live="polite"
                className="mt-3 font-mono uppercase tracking-[0.22em] text-right"
                style={{
                  fontSize: 10,
                  color:    isCopied ? "#7cf5c4" : t.accent,
                  transition: "color 180ms ease",
                  minHeight: 12,
                }}
              >
                {isCopied ? `✓ ${t.copiedLabel}` : (t.action === "popup" ? "Tune in →" : "Copy →")}
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}
